import ImageWithFallback from "@/components/ImageWithFallback";
import Link from "next/link";
import { Button } from "@/components/shadcn/button";
import { TemplateVisual } from "@/generated";
import { useEffect, useMemo } from "react";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { useSetAtom, useAtomValue, useAtom } from "jotai";
import { templateDisplayOpenAtom } from "@/app/(dashboard)/layout";
import { cn } from "@/lib/utils";
import { ExternalLink, FileText, Layers, Play, X } from "lucide-react";
import { useCurrentPage } from "./currentTool";
import { atomWithStorage } from "jotai/utils";
import { SpotMarkdown } from "@/components/Spotflow/SpotMarkdown/SpotMarkdown";
import { isRunModalOpenAtom } from "@/store/editor-jotai";
import { isAssistantRunnerOpenAtom } from "@/store/assistant-editor-jotai";
import { getColorFromTheme } from "@/utils/generic";
import { getIconComponent } from "@/utils/templates";
import { isEditorV2Enabled } from "@/utils/modules";

interface TemplateSubComponent {
  name: string;
  type: string;
  description?: string | null;
  icon?: string | null;
  sidenav_text?: string | null;
}

interface TemplateComponent {
  name: string;
  type: string;
  description: string;
  icon?: string | null;
  is_main?: boolean;
  sidenav_text?: string | null;
  sub_components?: TemplateSubComponent[];
}

interface ExtendedTemplateVisual extends TemplateVisual {
  long_description?: string | null;
  sidenav_runner_text?: string | null;
  components?: TemplateComponent[];
}

interface FlatComponent {
  name: string;
  type: string;
  description?: string;
  icon?: string | null;
  is_main?: boolean;
  sidenav_text?: string | null;
}

export const TemplateSideNav = () => {
  // const Icon = getIconComponent(template?.icon);
  const { spotGroup } = useSpotUser();
  const [templateDisplayOpen, setTemplateDisplayOpen] = useAtom(
    templateDisplayOpenAtom
  );
  const { id: currentPageId, type: currentPageType } = useCurrentPage();
  const isRunModalOpen = useAtomValue(isRunModalOpenAtom);
  const isAssistantRunnerOpen = useAtomValue(isAssistantRunnerOpenAtom);

  // Fetch current item data
  const {
    data: workflowData,
    isLoading: isLoadingWorkflows,
    isError: isErrorWorkflows,
  } = useRemoteData(
    DATA_KEYS.WORKFLOWS_ID,
    {
      path: {
        group_id: spotGroup.id,
        workflow_id: currentPageId || "",
      },
    },
    {
      enabled: !!currentPageId && currentPageType === "flow",
    }
  );

  const { data: assistantData, isLoading: isLoadingAssistant } = useRemoteData(
    DATA_KEYS.ASSISTANT_ID,
    {
      path: {
        group_id: spotGroup.id,
        assistant_id: currentPageId || "",
      },
    },
    {
      enabled: !!currentPageId && currentPageType === "assistant",
    }
  );

  const { data: knowledgeBaseData, isLoading: isLoadingKnowledgeBase } =
    useRemoteData(
      DATA_KEYS.KNOWLEDGE_BASES_V2_ID,
      {
        path: {
          group_id: spotGroup.id,
          knowledge_base_id: currentPageId || "",
        },
      },
      {
        enabled: !!currentPageId && currentPageType === "knowledge_base",
      }
    );

  const templateId = useMemo(() => {
    if (workflowData) return workflowData.meta?.template_id as string;
    if (assistantData) return assistantData.meta?.template_id as string;
    if (knowledgeBaseData) return knowledgeBaseData.meta?.template_id as string;
    return null;
  }, [workflowData, assistantData, knowledgeBaseData]);

  const { data: templateData, isLoading: isLoadingTemplate } = useRemoteData(
    DATA_KEYS.TEMPLATE_V2,
    {
      path: {
        group_id: spotGroup.id,
        template_id: templateId || "",
      },
    },
    {
      enabled: !!templateId,
    }
  );

  const isLoading = useMemo(() => {
    if (currentPageType === "flow") {
      return isLoadingWorkflows;
    }
    if (currentPageType === "assistant") {
      return isLoadingAssistant;
    }
    if (currentPageType === "knowledge_base") {
      return isLoadingKnowledgeBase;
    }
    return isLoadingTemplate;
  }, [
    currentPageType,
    isLoadingWorkflows,
    isLoadingAssistant,
    isLoadingKnowledgeBase,
    isLoadingTemplate,
  ]);

  const template = templateData as TemplateVisual | undefined;
  const Icon = getIconComponent(template?.icon);

  const currentItemName = useMemo(() => {
    if (workflowData) return workflowData.name;
    if (assistantData) return assistantData.name;
    if (knowledgeBaseData) return knowledgeBaseData.name;
    return null;
  }, [workflowData, assistantData, knowledgeBaseData]);

  // Check if current item belongs to this template
  const currentItemTemplateId = useMemo(() => {
    if (workflowData?.meta && "template_id" in workflowData.meta) {
      return (workflowData.meta as any).template_id;
    }
    if (assistantData && "meta" in assistantData) {
      const assistantMeta = (assistantData as any).meta;
      if (assistantMeta && "template_id" in assistantMeta) {
        return assistantMeta.template_id;
      }
    }
    if (knowledgeBaseData && "meta" in knowledgeBaseData) {
      const kbMeta = (knowledgeBaseData as any).meta;
      if (kbMeta && "template_id" in kbMeta) {
        return kbMeta.template_id;
      }
    }
    return null;
  }, [workflowData, assistantData, knowledgeBaseData]);

  // Only show if we're viewing a component from this template
  const shouldShow =
    currentItemTemplateId === template?.id && templateDisplayOpen;

  // Flatten components and find current one
  const { flatComponents, currentComponent } = useMemo(() => {
    const extendedTemplate = template as ExtendedTemplateVisual;
    const components: FlatComponent[] = [];

    if (extendedTemplate?.components) {
      extendedTemplate.components.forEach((component: TemplateComponent) => {
        // Add the main component
        const mainComp: FlatComponent = {
          name: component.name,
          type: component.type,
          description: component.description,
          icon: component.icon,
          is_main: component.is_main,
          sidenav_text: component.sidenav_text,
        };
        components.push(mainComp);

        // Add all sub-components
        if (component.sub_components) {
          component.sub_components.forEach((subComponent) => {
            components.push({
              name: subComponent.name,
              type: subComponent.type,
              description: subComponent.description || undefined,
              icon: subComponent.icon,
              is_main: false,
              sidenav_text: subComponent.sidenav_text,
            });
          });
        }
      });
    }

    // Find current component by matching the name
    const current = components.find((comp) => {
      if (!currentItemName) return false;
      // Simple name match - could be improved with fuzzy matching
      return comp.name.toLowerCase() === currentItemName.toLowerCase();
    });

    return {
      flatComponents: components,
      currentComponent: current,
    };
  }, [template, currentItemName]);

  // Runner is open when the run modal is open (for workflows) or assistant runner is open (for assistants)
  const isRunnerOpen = isRunModalOpen || isAssistantRunnerOpen;

  // Determine if we're viewing the main component
  const isViewingMainComponent = useMemo(() => {
    const result = currentComponent?.is_main === true;
    return result;
  }, [currentComponent]);

  // Determine what content to show in the sidenav
  const sidenavContent = useMemo(() => {
    const extendedTemplate = template as ExtendedTemplateVisual;

    // If we're on the main component and runner IS open, show runner text
    if (
      isViewingMainComponent &&
      isRunnerOpen &&
      extendedTemplate.sidenav_runner_text
    ) {
      return {
        type: "runner" as const,
        content: extendedTemplate.sidenav_runner_text,
      };
    }

    // If current component has sidenav_text, show that
    if (currentComponent?.sidenav_text) {
      return {
        type: "component" as const,
        content: currentComponent.sidenav_text,
      };
    }

    // Otherwise show the regular description
    return {
      type: "description" as const,
      content: currentComponent?.description || null,
    };
  }, [template, currentComponent, isViewingMainComponent, isRunnerOpen]);

  const handleClose = () => {
    setTemplateDisplayOpen(false);
  };

  const isOpen = !!(shouldShow && (template || isLoading));

  return (
    <div
      className={cn(
        "h-full bg-background flex flex-col relative shrink-0 transition-all duration-300",
        // The border only exists while open — closed, a bordered w-0 element
        // still occupies 2px and pinches a full-height sliver off the page.
        isOpen
          ? "w-72 lg:w-80 xl:w-96 opacity-100 py-2 px-2 z-1000 border border-border"
          : "w-0 opacity-0 overflow-hidden px-0 py-0 border-0"
      )}
    >
      <div className="flex flex-col h-full bg-background p-3 rounded-md bg">
        {/* Fixed Header */}
        <div className="flex items-center gap-2 mb-6 shrink-0">
          <div
            className={`${template?.color} w-8 h-8 rounded-sm flex items-center justify-center border border-border shrink-0`}
          >
            <Icon
              size={24}
              color={getColorFromTheme(`${template?.color}`)}
              strokeWidth={1.5}
            />
          </div>
          <h2 className="text-xl font-medium text-primary truncate grow">
            {template?.name}
          </h2>
          <Button variant="ghost" onClick={handleClose}>
            <X />
          </Button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Sidenav content (runner text or component-specific text) */}
          {sidenavContent.content &&
            (sidenavContent.type === "runner" ||
              sidenavContent.type === "component") && (
              <div className="text-xs leading-normal **:text-muted-foreground [&_strong]:font-medium [&_strong]:text-muted-foreground! [&_code]:text-xs [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_pre]:bg-muted [&_pre]:p-3 [&_pre]:rounded-md [&_pre]:text-xs [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_li]:ml-0 [&_p]:mb-3 [&_p]:text-muted-foreground">
                <SpotMarkdown
                  markdown={sidenavContent.content}
                  options={{
                    markdownClass: "",
                  }}
                />
              </div>
            )}
        </div>

        {/* Fixed Footer */}
        {!isEditorV2Enabled(spotGroup) && (
          <div className="mt-6 flex flex-col gap-2 shrink-0">
            <Link href="/templates" className="w-full">
              <Button variant="outline" className="w-full">
                <Layers />
                Browse templates
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
